import React from 'react'
import MuiTextField from '@mui/material/TextField'
import BaseDisplayItem, {
    BaseDisplayItemProps,
    DisplayItemBase,
    DisplayMode,
} from '@/feature/DisplayItems/BaseDisplayItem'

/* ---------- Types ---------- */

interface ItemSettings {
    decimals?: number // default 4
    allow_negative?: boolean // default true
    // Optional: show as currency (display only)
    currency?: string // e.g. "EUR"
    locale?: string // e.g. "nl-NL"
}

interface Item extends DisplayItemBase {
    settings?: ItemSettings
    mode?: DisplayMode
}

interface Props extends BaseDisplayItemProps<string, Item> {
    onKeyPress?: (
        itemOrId: Item | Item['id'],
        event: React.KeyboardEvent<HTMLInputElement>
    ) => void
}

/* ---------- Helpers ---------- */

function normalizeDecimalInput(
    raw: string,
    decimals: number,
    allowNegative: boolean
): string {
    if (raw == null) return ''

    // Trim + normalize comma to dot
    let v = raw.trim().replace(',', '.')

    // Remove everything except digits, dot, minus
    v = v.replace(/[^\d.-]/g, '')

    // Only allow a single leading "-"
    if (!allowNegative) {
        v = v.replace(/-/g, '')
    } else {
        v = v.replace(/(?!^)-/g, '') // remove any "-" not at start
    }

    // Only allow one dot
    const firstDot = v.indexOf('.')
    if (firstDot !== -1) {
        const before = v.slice(0, firstDot + 1)
        const after = v.slice(firstDot + 1).replace(/\./g, '') // remove extra dots
        v = before + after
    }

    // Enforce max decimals
    const dot = v.indexOf('.')
    if (dot !== -1 && decimals >= 0) {
        const intPart = v.slice(0, dot)
        const decPart = v.slice(dot + 1).slice(0, decimals)
        v = `${intPart}.${decPart}`
    }

    return v
}

function formatForShow(
    value: string,
    locale?: string,
    currency?: string
): string {
    // Display only. Keep value as string for storage/updates.
    const n = Number(value)
    if (!Number.isFinite(n)) return value

    if (currency) {
        return new Intl.NumberFormat(locale ?? 'nl-NL', {
            style: 'currency',
            currency,
        }).format(n)
    }

    return new Intl.NumberFormat(locale ?? 'nl-NL', {
        maximumFractionDigits: 20,
    }).format(n)
}

/* ---------- Component ---------- */

class DecimalField extends BaseDisplayItem<string, Item, HTMLInputElement> {
    constructor(props: Props) {
        super(props)
        this.onChange = this.onChange.bind(this)
        this.onBlur = this.onBlur.bind(this)
    }

    private getDecimals(): number {
        return this.getSetting<number>('decimals', {
            defaultValue: 4,
            source: 'item',
        })
    }

    private getAllowNegative(): boolean {
        return this.getSetting<boolean>('allow_negative', {
            defaultValue: true,
            source: 'item',
        })
    }

    /* ---------- Change ---------- */

    onChange(event: React.ChangeEvent<HTMLInputElement>) {
        const raw = event.target.value
        const decimals = this.getDecimals()
        const allowNegative = this.getAllowNegative()

        const normalized = normalizeDecimalInput(raw, decimals, allowNegative)

        // Keep BaseDisplayItem state + parent in sync
        this.updateValue(normalized)
    }

    /**
     * Optional: on blur you can canonicalize to fixed decimals.
     * For DECIMAL(12,4) this can be useful so the API always sees "12.3400".
     * If you don't want forced padding, remove this method + onBlur prop.
     */
    onBlur() {
        const decimals = this.getDecimals()
        const v = (this.state.value ?? '').trim()

        if (v === '' || v === '-' || v === '.' || v === '-.') {
            this.updateValue('')
            return
        }

        // If it parses, pad to fixed decimals
        const n = Number(v)
        if (!Number.isFinite(n)) return

        // Use toFixed for padding (display/normalization only).
        // Backend remains source of truth.
        const fixed = n.toFixed(decimals)

        // Keep minus at front, dot separator
        this.updateValue(fixed)
    }

    /* ---------- Render: SHOW ---------- */

    renderShow(): React.ReactNode {
        const props = this.props as Props
        if (!props.value) return null

        const n = Number(props.value)

        if (!Number.isFinite(n)) {
            return props.value
        }

        return new Intl.NumberFormat('nl-NL', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 4, // match your DB scale
        }).format(n)
    }

    /* ---------- Render: EDIT ---------- */

    renderEdit(): React.ReactNode {
        const props = this.props as Props
        const decimals = this.getDecimals()

        return (
            <MuiTextField
                size="small"
                fullWidth
                inputRef={this.innerRef}
                value={this.state.value ?? ''}
                error={this.isInvalid()}
                helperText={this.isInvalid() ? this.getErrorText() : ''}
                onChange={this.onChange}
                onBlur={this.onBlur}
                onKeyPress={
                    props.onKeyPress
                        ? (e) => props.onKeyPress!(props.item, e)
                        : undefined
                }
                inputProps={{
                    inputMode: 'decimal',
                    pattern: '^-?\\d*[.,]?\\d*$',
                    // maxLength: choose something sane for precision=12, scale=4 plus sign/dot
                    maxLength: 1 + (12 - decimals) + 1 + decimals, // sign + int + dot + dec
                }}
                placeholder={decimals > 0 ? `0.${'0'.repeat(decimals)}` : '0'}
            />
        )
    }

    /* ---------- Render: SEARCH ---------- */

    renderSearch(): React.ReactNode {
        const props = this.props as Props
        const decimals = this.getDecimals()
        const allowNegative = this.getAllowNegative()

        return (
            <MuiTextField
                size="small"
                fullWidth
                type="text"
                value={props.value ?? ''}
                onChange={(e) => {
                    const normalized = normalizeDecimalInput(
                        e.target.value,
                        decimals,
                        allowNegative
                    )
                    props.changeValue?.(props.item.id, normalized, props.item)
                }}
                onKeyPress={
                    props.onKeyPress
                        ? (e) => props.onKeyPress!(props.item.id, e)
                        : undefined
                }
                inputProps={{
                    inputMode: 'decimal',
                    pattern: '^-?\\d*[.,]?\\d*$',
                }}
            />
        )
    }
}

export default DecimalField
